import { useCallback, useRef, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import {
    type SupportedCountry,
    FindPlaceApiProviderEnum,
    FindPlaceTypeEnum,
} from 'generated/graphql';
import { FIND_PLACE_BY_KEYWORD_ACTION } from '~/routes/actions.location.find-place-by-keyword';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

interface UsePlaceFinderDirectProps {
    type?: FindPlaceTypeEnum;
    country: SupportedCountry;
    apiProvider?: FindPlaceApiProviderEnum;
    /**
     * When provided, selects the API provider per search based on the keyword.
     * Useful for postcode searches which should use ServiceableArea (returns all suburbs)
     * while address/suburb searches use Mapbox.
     */
    apiProviderSelector?: (keyword: string) => FindPlaceApiProviderEnum;
}

/**
 * Hook to search for a list of locations by keyword using direct fetch instead of Remix fetcher.
 * This is useful in contexts where useFetcher doesn't work well (e.g., inside Popovers).
 * @todo Merge with `usePlaceFinder` in modules/location.
 */
export const usePlaceFinderDirect = (props: UsePlaceFinderDirectProps) => {
    const {
        type = FindPlaceTypeEnum.SuburbMixed,
        country,
        apiProvider = FindPlaceApiProviderEnum.ServiceableArea,
        apiProviderSelector,
    } = props;

    const [places, setPlaces] = useState<DetailedPlace[]>([]);
    const [loading, setLoading] = useState(false);
    const isRequestInFlightRef = useRef(false);
    const searchCountry = country;

    const updateKeyword = useCallback(
        async (keyword: string, limit: number) => {
            if (!keyword || keyword.trim().length < 2) {
                setPlaces([]);
                setLoading(false);
                isRequestInFlightRef.current = false;
                return;
            }

            if (isRequestInFlightRef.current) {
                return;
            }

            isRequestInFlightRef.current = true;
            setLoading(true);

            try {
                const provider =
                    apiProviderSelector?.(keyword.trim()) ?? apiProvider;
                const params = new URLSearchParams({
                    keyword: keyword.trim(),
                    type: type.toString(),
                    limit: limit.toString(),
                    country: searchCountry.toString(),
                    apiProvider: provider.toString(),
                });

                const response = await fetch(
                    `${FIND_PLACE_BY_KEYWORD_ACTION}?${params.toString()}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    },
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setPlaces(data ?? []);
            } catch (error) {
                console.error('Error fetching places:', error);
                setPlaces([]);
            } finally {
                setLoading(false);
                isRequestInFlightRef.current = false;
            }
        },
        [searchCountry, type, apiProvider, apiProviderSelector],
    );

    const { run: handlePlaceFind } = useDebounceFn(updateKeyword, {
        wait: 500,
    });

    const resetPlaces = useCallback(() => {
        setPlaces([]);
        setLoading(false);
    }, []);

    return {
        places,
        loading,
        handlePlaceFind,
        resetPlaces,
    };
};
